import { FC, useEffect } from 'react'
import { Calendar, Download } from 'lucide-react'
import DynamicIcon from '../../../components/DynamicIcon'
import { useIntl } from 'react-intl'
import ConvertDate from '../../../Utils/ConvertDate'
import { Button } from 'react-bootstrap'
import { useReportHistoryInfinite } from '../../../hooks/queries'
import { useReportDownload } from '../../../hooks/queries/useReports'

const ReportsHistoryWidget: FC = () => {
  const intl = useIntl()

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useReportHistoryInfinite()
  const { mutate: download } = useReportDownload()

  // Flatten all pages into a single array of reports
  const allReports = data?.pages.flatMap(page => page.results) || []
  // Auto-refetch every 10 seconds if there are pending reports
  useEffect(() => {
    const hasPendingReports = allReports.some(report => report.status === 'PENDING')
    if (hasPendingReports) {
      const interval = setInterval(() => {
        refetch()
      }, 10000)

      return () => clearInterval(interval)
    }
  }, [allReports, refetch])
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'PENDING':
        return {
          icon: 'Clock',
          color: '#ffc107',
          bgColor: 'bg-warning',
          text: 'In production...',
        }
      case 'FAILED':
        return {
          icon: 'CircleX',
          color: 'red',
          bgColor: 'bg-danger',
          text: 'Failed',
        }
      case 'COMPLETED':
        return {
          icon: 'CircleCheck',
          color: '#28a745',
          bgColor: 'bg-success',
          text: 'Ready to download',
        }
      default:
        return {
          icon: 'File',
          color: '#6c757d',
          bgColor: 'bg-secondary',
          text: 'Unknown',
        }
    }
  }

  const getTypeInfo = (type: string) => {
    switch (type) {
      case 'pdf': return 'PDF'
      case 'html': return 'HTML'
      case 'xlsx': return 'Excel'
      case 'docx': return 'Word'
      default: return type
    }
  }

  return (
    <div
      className='bg-transparent w-350px w-lg-375px container-fluid menu menu-sub menu-sub-dropdown menu-column'
      data-kt-menu='true'
    >
      <div className='row'>
        <div className='col-12'>
          <div
            className='shadow-lg border-0 card'
            style={{
              width: '380px',
              borderRadius: '15px',
            }}
          >
            {/* Heder */}
            <div
              className='d-flex justify-content-center align-items-center text-center text-white card-header'
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '15px 15px 0 0',
              }}
            >
              <h1 className='mb-0 text-white fw-bold'>{intl.formatMessage({ id: 'Reports' })}</h1>
            </div>

            {/* Main */}
            <div
              className='py-4 p-0 min-h-100px card-body mh-400px over_x_y'
              style={{ borderRadius: '0 0 15px 15px' }}
            >
              {allReports.map((report) => {
                const statusInfo = getStatusInfo(report.status)
                const typeInfo = getTypeInfo(report.file_type)

                return (
                  <div
                    key={report.id}
                    className='position-relative border-bottom d-flex align-items-center p-3 border-light'
                    style={{
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                    }}
                  >
                    {/* Status Indicator */}
                    <div
                      className='top-0 position-absolute w-1 h-100 start-0'
                      style={{ backgroundColor: statusInfo.color }}
                    ></div>

                    {/* Status Icon */}
                    <div className='me-3'>
                      <div
                        className='d-flex justify-content-center align-items-center rounded-circle'
                        style={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: statusInfo.color + '20',
                          border: `2px solid ${statusInfo.color}`,
                          fontSize: '18px',
                        }}
                      >
                        <DynamicIcon name={statusInfo.icon} size={18} color={statusInfo.color} strokeWidth={2.25} />
                      </div>
                    </div>

                    {/*  Info */}
                    <div className='flex-grow-1 me-2'>
                      <div
                        className='mb-1 fw-bold'
                        style={{
                          fontSize: '14px',
                          lineHeight: '1.3',
                        }}
                      >
                        {report.display_name}
                      </div>

                      <div
                        className='d-flex align-items-center gap-1 mb-1 text-muted'
                        style={{ fontSize: '12px' }}
                      >
                        <Calendar size={16} />{''}
                        {ConvertDate({ date: report.created_at })}
                      </div>

                      <div className='d-flex align-items-center gap-2'>
                        <div>
                          <span
                            className={`badge ${statusInfo.bgColor} text-white`}
                            style={{ fontSize: '10px' }}
                          >
                            {intl.formatMessage({ id: statusInfo.text })}
                          </span>
                        </div>
                        <div>
                          <span
                            className={`badge bg-primary text-white`}
                            style={{ fontSize: '10px' }}
                          >
                            {intl.formatMessage({ id: report.lang === 'en' ? 'English' : 'Persian' })}
                          </span>
                        </div>
                        <div>
                          <span className={`badge bg-info text-white`} style={{ fontSize: '10px' }}>
                            {typeInfo}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className='d-flex align-items-center gap-2'>
                      {/* Info Icon with Tooltip */}

                      {report.status === 'COMPLETED' ? (

                        <button
                          className='d-flex justify-content-center align-items-center m-0 p-0 rounded-circle btn btn-success btn-sm'
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)'
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(40, 167, 69, 0.3)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)'
                            e.currentTarget.style.boxShadow = 'none'
                          }}
                          onClick={() => { download({ id: report.id, name: `${report.display_name}.${report.file_type}` }) }}
                        >
                          <Download size={18} className='m-0 p-0 text-white' />
                        </button>

                      ) : report.status === 'PENDING' ? (
                        <div
                          className='p-2 rounded-circle btn btn-outline-warning btn-sm disabled'
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            className='spinner-border text-warning'
                            role='status'
                            style={{ width: '14px', height: '14px' }}
                          >
                            <span className='visually-hidden'>Loading...</span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })}
              {hasNextPage ? (
                <div className='d-flex justify-content-center mt-4'>
                  <Button
                    disabled={isFetchingNextPage}
                    variant='outline-primary'
                    size='sm'
                    onClick={() => fetchNextPage()}
                  >
                    {isFetchingNextPage ? (
                      <span className='spin'> {intl.formatMessage({ id: 'loading' })} ... </span>
                    ) : (
                      intl.formatMessage({ id: 'more' })
                    )}
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { ReportsHistoryWidget as Reports }
